import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { healthCommitteeApi } from "../../services/healthCommitteeApi";
import { employeeApi } from "../../services/employeeApi";
import { usePermissions } from "../../hooks/usePermissions";
import { formatApiErrorDetail } from "../../lib/api";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

const MEETING_TYPES = ["REGULAR", "EXTRAORDINARY"];
const EMPTY_FORM = { meeting_date: "", start_time: "", end_time: "", location: "", meeting_type: "REGULAR", chairperson_employee_id: "", recorder_employee_id: "" };

export default function HealthCommitteeMeetingFormPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canManageHealthCommittee } = usePermissions();
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => { employeeApi.list().then((r) => setEmployees(r.data)).catch(() => {}); }, []);

  if (!canManageHealthCommittee) return <p className="text-sm text-gray-500" data-testid="health-committee-access-denied">{t("common.accessDenied")}</p>;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, chairperson_employee_id: form.chairperson_employee_id || null, recorder_employee_id: form.recorder_employee_id || null };
      const { data } = await healthCommitteeApi.createMeeting(payload);
      toast.success(t("mgmtHealthCommittee.meetingCreated"));
      navigate(`/management/compliance/health-committee/meetings/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="health-committee-meeting-form-page">
      <PageHelpBanner pageKey="health-committee-meeting-form" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtHealthCommittee.meetingFormHelp")}</p>
      </PageHelpBanner>
      <Link to="/management/compliance/health-committee/meetings" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="health-committee-back-link">&larr; {t("mgmtHealthCommittee.backToMeetings")}</Link>
      <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtHealthCommittee.newMeetingTitle")}</h2>

      <form onSubmit={submit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtHealthCommittee.meetingDateRequired")}</label>
            <input data-testid="hc-meeting-date-input" required type="date" value={form.meeting_date} onChange={(e) => setForm((f) => ({ ...f, meeting_date: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtHealthCommittee.colType")}</label>
            <select data-testid="hc-meeting-type-select" value={form.meeting_type} onChange={(e) => setForm((f) => ({ ...f, meeting_type: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
              {MEETING_TYPES.map((mt) => <option key={mt} value={mt}>{mt}</option>)}
            </select>
          </div>
          <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtHealthCommittee.startTime")}</label>
            <input data-testid="hc-start-time-input" type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtHealthCommittee.endTime")}</label>
            <input data-testid="hc-end-time-input" type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtHealthCommittee.colLocation")}</label>
          <input data-testid="hc-location-input" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtHealthCommittee.chairperson")}</label>
            <select data-testid="hc-chairperson-select" value={form.chairperson_employee_id} onChange={(e) => setForm((f) => ({ ...f, chairperson_employee_id: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <option value="">{t("mgmtHealthCommittee.selectEllipsis")}</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
            </select>
          </div>
          <div><label className="block text-xs text-gray-500 mb-1">{t("mgmtHealthCommittee.recorder")}</label>
            <select data-testid="hc-recorder-select" value={form.recorder_employee_id} onChange={(e) => setForm((f) => ({ ...f, recorder_employee_id: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <option value="">{t("mgmtHealthCommittee.selectEllipsis")}</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
            </select>
          </div>
        </div>
        <button data-testid="hc-create-meeting-btn" disabled={saving} type="submit" className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
          {saving ? t("mgmtHealthCommittee.creatingEllipsis") : t("mgmtHealthCommittee.newMeeting")}
        </button>
      </form>
    </div>
  );
}
